import React, { useState, useCallback, useEffect } from 'react';

// Bootstrap
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Custom CSS
import './styles/variables.css';
import './styles/mobile.css';
import './styles/desktop.css';
import './styles/mobile-light.css';
import './styles/mobile-dark.css';
import './styles/navbar-bottom.css';
import './App.css';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { TournamentProvider, useTournament } from './contexts/TournamentContext';

// Components
import ToastContainer from './components/common/ToastContainer';
import ScrollWizardPage from './pages/ScrollWizardPage';
import LoginModal from './components/modals/LoginModal';
import SignOutModal from './components/modals/SignOutModal';
import TournamentListModal from './components/modals/TournamentListModal';
import QRCodeModal from './components/modals/QRCodeModal';
import LoginBanner from './components/common/LoginBanner';
import AuthRequiredModal from './components/modals/AuthRequiredModal';

// Hooks
import useFirebaseAutoSave from './hooks/useFirebaseAutoSave';
import firebaseSyncService from './services/firebaseSync';

function AppContent() {
  const { currentUser, loading, guestMode, signOut, handleAuthCallback, isSignedIn } = useAuth();
  const { showSuccess, showError } = useToast();
  const { state, importState, resetTournament } = useTournament();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showTournamentsModal, setShowTournamentsModal] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);

  // Handle auth callback token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      handleAuthCallback(token).then((result) => {
        if (!result.success) {
          console.error('Auth callback failed:', result.error);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show/hide login modal based on auth state
  useEffect(() => {
    if (!loading) {
      if (!isSignedIn() && !guestMode) {
        setShowLoginModal(true);
      } else {
        setShowLoginModal(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentUser, guestMode]);

  // Firebase auto-save (only for authenticated users)
  const { isSyncing, lastSyncTime } = useFirebaseAutoSave(
    currentUser ? state : null,
    currentUser ? currentUser.email : null,
    null  // screenName — will pass active section later
  );

  // Sign out
  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      await signOut();
      firebaseSyncService.clearCurrentTournament();
      resetTournament();
      setShowSignOutModal(false);
      showSuccess('Déconnexion réussie');
    } catch (err) {
      showError('Erreur lors de la déconnexion');
    } finally {
      setSignOutLoading(false);
    }
  };

  // Load tournament from list
  const handleLoadTournament = useCallback((data) => {
    if (data?.state) {
      importState(data.state);
    }
  }, [importState]);

  // Reset (new tournament)
  const handleReset = () => {
    if (window.confirm('Créer un nouveau tournoi ? Les données non sauvegardées seront perdues.')) {
      firebaseSyncService.clearCurrentTournament();
      resetTournament();
      showSuccess('Nouveau tournoi créé');
    }
  };

  // Sign-out button: show login if guest, show sign-out modal if authenticated
  const handleSignOutClick = () => {
    if (guestMode) {
      setShowLoginModal(true);
    } else {
      setShowSignOutModal(true);
    }
  };

  return (
    <>
      <LoginBanner />

      <ScrollWizardPage
        onTournamentsClick={() => isSignedIn() ? setShowTournamentsModal(true) : setShowAuthRequiredModal(true)}
        onResetClick={handleReset}
        onSignOutClick={handleSignOutClick}
        onQRCodeClick={() => setShowQRCodeModal(true)}
        isSyncing={isSyncing}
        lastSyncTime={lastSyncTime}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      <SignOutModal
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={handleSignOut}
        loading={signOutLoading}
      />

      <TournamentListModal
        isOpen={showTournamentsModal}
        onClose={() => setShowTournamentsModal(false)}
        onLoad={handleLoadTournament}
      />

      <AuthRequiredModal
        isOpen={showAuthRequiredModal}
        onClose={() => setShowAuthRequiredModal(false)}
      />

      <QRCodeModal
        isOpen={showQRCodeModal}
        onClose={() => setShowQRCodeModal(false)}
      />

      <ToastContainer />
    </>
  );
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <TournamentProvider>
          <AppContent />
        </TournamentProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
